import { useTranslation } from "react-i18next";
import { Grid, Skeleton, IconButton } from "@mui/material";
import MUIDataTable from "mui-datatables";
import NoMatchImage from "../../../components/common/NoMatchImage/noMatchImage";
import { NoInfoComponent } from "../../../components/common/NoInfoImage/noInfoComponent";
import { useEffect, useState } from "react";

import { FilterUsers } from "../../../components/common/infonavit/users/filterUsers";
import { getUser, getUserStatus } from "../../userSlice";
import { useSelector } from "react-redux";
import { CODES } from "../../../consts/codes";

import CreateUser from "../../../components/common/infonavit/users/createUser";
import RolViewerModal from "../../../components/common/infonavit/users/rolViewerModal";
import EditUser from "../../../components/common/infonavit/users/editUser";

import { FilterUsersClient } from "../../../services/user/consultRolesService";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ModalSeeCompanies from "../../../components/common/AdminUsersClient/modalSeeCompanies";
import jwt_decode from "jwt-decode";
import PeriodicReportState from "../../../components/common/DynamicReport/periodicReportState";
import CustomTablePagination from "../../../components/DataTable/CustomTablePagination";

const AdminUsers = () => {
  /**
   * Tomando el rol por el token
   */
  const jwtTokenInfo = localStorage.getItem("infoToken");

  let role = "";
  if (jwtTokenInfo) {
    const jwtDecodeTokenInfo = jwt_decode(jwtTokenInfo);

    role = jwtDecodeTokenInfo["custom:Role"];
  }

  const userInfo = useSelector(getUser);
  const userStatus = useSelector(getUserStatus);

  const [data, setData] = useState([]);
  const [unfilteredData, setUnfilteredData] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [noContent, setNoContent] = useState(false);

  const [openEditUser, setOpenEditUser] = useState(false);
  const [currentUserEdit, setCurrentUserEdit] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    permissions: [],
  });

  const [currentCompany, setCurrentCompany] = useState({
    eanCompany: "",
    country: "",
    companyName: "",
  });

  const { t } = useTranslation();

  const columns = [
    {
      name: "firstName",
      label: t("App.infonavitUsers.table.firstName"),
      options: {
        filter: false,
        sort: true,
        searchable: true,
      },
    },
    {
      name: "lastName",
      label: t("App.infonavitUsers.table.lastName"),
      options: {
        filter: false,
        sort: true,
        searchable: true,
      },
    },
    {
      name: "email",
      label: t("App.infonavitUsers.table.email"),
      options: {
        filter: false,
        sort: false,
        searchable: true,
      },
    },
    {
      name: "phone",
      label: t("App.infonavitUsers.table.phone"),
      options: {
        filter: false,
        sort: false,
        searchable: false,
      },
    },
    {
      name: "listCompanies",
      label: t("App.infonavitUsers.table.enterprise"),
      options: {
        filter: false,
        sort: false,
        searchable: false,
      },
    },
    {
      name: "menuOptionName",
      label: t("App.infonavitUsers.table.menuOptionName"),
      options: {
        filter: false,
        sort: true,
        searchable: false,
      },
    },
    {
      name: "nameRole",
      label: t("App.infonavitUsers.table.nameRole"),
      options: {
        filter: false,
        sort: true,
        searchable: false,
      },
    },

    {
      name: "state",
      label: t("App.infonavitUsers.table.state"),
      options: {
        filter: false,
        sort: true,
        searchable: false,
        customBodyRender: (value) => <PeriodicReportState value={value} />,
      },
    },
    {
      name: "edit",
      label: t("App.infonavitUsers.table.edit"),
      options: {
        filter: false,
        sort: false,
        searchable: false,
      },
    },
  ];

  const options = {
    filterType: "dropdown",
    print: "false",
    filter: false,
    download: false,
    search: false,
    selectableRows: "none",
    responsive: "standard",
    searchPlaceholder: t("App.userDetail.searchPlaceHolder"),
    elevation: 0,
    viewColumns: false,
    searchOpen: true,
    rowsPerPageOptions: [10, 15, 20],
    textLabels: {
      body: {
        noMatch: noContent ? (
          <NoMatchImage />
        ) : isLoadingData ? (
          <Skeleton />
        ) : (
          <NoInfoComponent type="dynamic" text="" />
        ),
        toolTip: "Sort",

        columnHeaderTooltip: (column) =>
          t("App.buttonLabels.order", { label: column.label }),
      },
      pagination: {
        next: t("App.userDetail.next"),
        previous: t("App.userDetail.previous"),
        rowsPerPage: t("App.userDetail.resultsPerPage"),
        displayRows: t("App.userDetail.of"),
      },
    },
  };

  /**
   * Use Effect
   */
  /**
   * Carga los usuarios
   */
  useEffect(() => {
    if (userStatus === "succeeded") fetchUsers();
  }, [userStatus]);

  /**
   * Función que consulta y settea la información de los usuarios
   */
  const fetchUsers = async () => {
    try {
      const eanCompany = userInfo.company.eanCompany;

      setCurrentCompany({
        eanCompany: userInfo.company.eanCompany,
        country: userInfo.company.country,
        companyName: userInfo.company.companyName,
      });

      setIsLoadingData(true);

      const userService = await FilterUsersClient({ eanCompany: eanCompany });

      const { responseCode, responseMessage } = userService.data;

      if (responseCode === CODES.COD_RESPONSE_SUCCESS_REQUEST) {
        const dataUsers = setDataStructureArray(responseMessage);

        setData(dataUsers);
        setUnfilteredData(dataUsers);
      } else {
        setData([]);
      }

      setIsLoadingData(false);
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  /**
   * Función que formatea los permisos del usuario
   * @param {*} user usuario
   * @param {*} rawData lista de usuarios
   * @returns lista de permisos del usuario
   */
  const definePermissionsArray = (user, rawData) => {
    // Busco los permisos del usuario actual en la lista de usuarios
    const userPermissions = rawData.filter(
      (record) => record.email === user.email
    );

    //Construyo los permisos del usuario
    const finalPermissions = userPermissions.map((userData) => {
      return {
        idMenuOption: userData.idMenuOption,
        menuOptionName: userData.menuOptionName,
        companyList: userData.lstCompanies,
        roleList: `{"idRole": "${userData.idRole}", "nameRole": "${userData.nameRole}"}`,
        state: userData.state,
      };
    });

    return finalPermissions;
  };

  /**
   * Función que settea el usuario para editarlo
   * @param {*} currentUser usuario actual
   */
  const handleOpenEdit = (currentUser) => {
    setCurrentUserEdit({ ...currentUser, randomIdTrigger: generateId() });
    setOpenEditUser(true);
  };

  /**
   * Función que formatea el arreglo de usuarios a el formato de tabla
   * @param {*} data arreglo de usuarios
   * @returns arreglo formateado
   */
  const setDataStructureArray = (data) => {
    const formattedData = data.map((clientUser) => {
      const itemForEdit = {
        ...clientUser,
        permissions: definePermissionsArray(clientUser, data),
      };

      return {
        firstName: clientUser.firstName,
        lastName: clientUser.lastName,
        email: clientUser.email,
        phone: clientUser.phone,
        listCompanies:
          clientUser.lstCompanies?.length > 1 ? (
            <ModalSeeCompanies
              data={clientUser.lstCompanies}
              userName={`${clientUser.firstName} ${clientUser.lastName}`}
            />
          ) : (
            clientUser.lstCompanies[0].renameCompany
          ),
        nameRole: clientUser.nameRole,
        menuOptionName: clientUser.menuOptionName,
        state: clientUser.state === 1 ? "Activo" : "Inactivo",
        edit: (
          <IconButton
            onClick={() => {
              handleOpenEdit(itemForEdit);
            }}
          >
            <EditOutlinedIcon fontSize={"24px"} style={{ color: "#543ab4" }} />
          </IconButton>
        ),
      };
    });

    return formattedData;
  };

  /**
   * Función que genera un id random
   * @returns id random
   */
  const generateId = () => {
    const id = Date.now().toString(32) + Math.random().toString(32);
    return id;
  };

  return (
    <Grid
      container
      gap={{ xs: "2rem", md: "3rem" }}
      padding={{
        xs: "2rem",
        sm: "2.5rem 3rem",
        md: "2.5rem 3rem",
        lg: "4.2rem 4.5rem",
        xl: "4.8rem 4.5rem",
      }}
    >
      <Grid item xs={12} style={{ paddingLeft: 0, paddingTop: 0 }}>
        <p className="display-large__primary-one">
          {t("App.infonavitUsers.title")}
        </p>
      </Grid>

      <Grid item xs={12}>
        <Grid className="companies_info_container">
          <Grid item>
            <Grid container mb="1.5rem" justifyContent="flex-end">
              <RolViewerModal />

              <div style={{ width: "2rem" }}></div>

              <CreateUser
                clientUser={currentUserEdit}
                handleOpenEdit={handleOpenEdit}
                currentCompany={currentCompany}
                companyId={currentCompany?.eanCompany}
                country={currentCompany?.country}
                role={role}
                fetchUsers={fetchUsers}
              />
            </Grid>

            <FilterUsers
              setDataStructureArray={setDataStructureArray}
              setData={setData}
              setIsLoadingData={setIsLoadingData}
              setNoContent={setNoContent}
              unfilteredData={unfilteredData}
              data={data}
            />
          </Grid>

          <br />
          <br />
          <br />

          <Grid item>
            <MUIDataTable
              className="dataTable__base datatable-style"
              style={{ marginTop: "5%", display: "none" }}
              components={{
                TableFooter: (props) => (
                  <CustomTablePagination paginationProps={props} />
                ),
              }}
              data={data}
              columns={columns}
              options={options}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* °° Grid principal °° */}
      <EditUser
        openEditUser={openEditUser}
        setOpenEditUser={setOpenEditUser}
        clientUser={currentUserEdit}
        role={role}
        currentCompany={currentCompany}
        companyId={currentCompany?.eanCompany}
        country={currentCompany?.country}
        fetchUsers={fetchUsers}
      />
    </Grid>
  );
};

export default AdminUsers;
